import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { downloadExport } from "@/lib/api";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState, Btn, InfoTip } from "@/components/common";
import { StatusChip } from "@/components/StatusChip";
import { money, taxYears, fmtDate } from "@/lib/format";
import { useParties, nickFor, expenseRoutingLabel } from "@/lib/parties";
import { ListChecks, CurrencyDollar, Bank, ArrowsLeftRight, DownloadSimple, FilePdf, ArrowRight, Funnel, Info, CaretDown, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const TYPE_META = {
  expense: { icon: CurrencyDollar, color: "#034AA2", label: "Expense" },
  withdrawal: { icon: Bank, color: "#6b7280", label: "529 Distribution" },
  settlement: { icon: ArrowsLeftRight, color: "#077A39", label: "Settlement" },
};
const FILTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "expense", label: "Expenses" },
  { key: "withdrawal", label: "Distributions" },
  { key: "settlement", label: "Settlements" },
];

export default function Ledger() {
  const { user, selectedStudentId } = useAuth();
  const sid = user.role === "student" ? user.student_id : selectedStudentId;
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [typeFilter, setTypeFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const isOwner = user.role === "owner";

  const { data, isLoading } = useQuery({
    queryKey: ["ledger", sid, year],
    queryFn: async () => (await api.get(`/ledger?student_id=${sid}${year ? `&tax_year=${year}` : ""}`)).data,
    enabled: !!sid,
  });
  const { data: parties } = useParties();
  const { data: studentObj } = useQuery({ queryKey: ["student", sid], queryFn: async () => (await api.get(`/students/${sid}`)).data, enabled: !!sid });
  const studentNick = studentObj?.nickname || studentObj?.full_name?.split(" ")[0] || "Beneficiary";

  if (!sid) return <div><PageHeader title="Money Trace" /><EmptyState icon={ListChecks} title="Select a beneficiary" subtitle="Choose a beneficiary from the selector at the top right to view their money trail." /></div>;

  const rec = data?.reconciliation;
  const exportCsv = () => {
    const params = { student_id: sid };
    if (year) params.tax_year = year;
    downloadExport("csv", params).catch((e) => toast.error(apiErr(e)));
  };
  const exportPdf = () => {
    const params = { student_id: sid };
    if (year) params.tax_year = year;
    downloadExport("pdf", params).catch((e) => toast.error(apiErr(e)));
  };

  const entries = (data?.entries || []).filter((en) => typeFilter === "all" || en.type === typeFilter);

  return (
    <div>
      <PageHeader
        title="Money Trace"
        subtitle={isOwner
          ? "The audit trail — every expense, distribution, and repayment for this beneficiary in one timeline. This is the story you'd show the IRS."
          : "This is the running history of what you've spent and what's been paid back to you."}
        actions={
          <div className="flex flex-wrap gap-2">
            <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="ledger-year" className="h-9 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm font-semibold"><option value="">All tax years</option>{taxYears().map((y) => <option key={y}>{y}</option>)}</select>
            <Btn variant="ghost" onClick={exportCsv} data-testid="ledger-export"><DownloadSimple size={16} weight="bold" /> CSV</Btn>
            <Btn variant="ghost" onClick={exportPdf} data-testid="ledger-export-pdf"><FilePdf size={16} weight="bold" /> PDF</Btn>
          </div>
        } />

      {rec && (
        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-[#C2C9D1] bg-white px-4 py-3" data-testid="ledger-summary-row">
          <SummaryFig label="529-eligible expenses" v={rec.total_eligible} c="#077A39" testid="summary-eligible" />
          <SummaryFig label="Total withdrawn" v={rec.total_withdrawn} c="#6b7280" testid="summary-withdrawn" />
          <SummaryFig label="529 settlements" v={rec.total_529_outstanding} c="#034AA2" testid="summary-529" />
          <Link to="/" className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-[#034AA2] hover:underline" data-testid="summary-dashboard-link">
            Full breakdown on the Overview <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      )}

      {isOwner && <FxReviewBanner studentId={sid} />}

      {rec && isOwner && (
        <Card className="mb-4 border-[#077A39]" data-testid="recon-principle">
          <div className="text-sm">
            <b className="text-[#077A39]">Reconciliation principle:</b> the amount reimbursed to the beneficiary equals what the beneficiary actually spent on 529-eligible items.
            529 outstanding to payors <b>{money(rec.total_529_outstanding, "USD")}</b> routes through {studentNick}; student-paid 529-eligible <b>{money(rec.student_paid_529_eligible, "USD")}</b> they keep; 529 direct-paid <b>{money(rec.direct_529_to_institution, "USD")}</b> creates no transfer.
          </div>
        </Card>
      )}

      <div className="mb-3 flex items-center gap-1.5" data-testid="timeline-filter">
        <Funnel size={15} weight="bold" className="text-[#6b7280]" />
        {FILTER_OPTIONS.map((f) => (
          <button key={f.key}
            onClick={() => setTypeFilter(f.key)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${typeFilter === f.key ? "bg-[#034AA2] text-white" : "bg-[#E9E9EB] text-[#374151] hover:bg-[#C2C9D1]"}`}
            data-testid={`filter-${f.key}`}
          >{f.label}</button>
        ))}
      </div>

      {isLoading ? <div className="h-64 animate-pulse rounded-xl bg-white" /> : (entries.length === 0 ? (
        <EmptyState icon={ListChecks} title={typeFilter !== "all" ? `No ${FILTER_OPTIONS.find(f => f.key === typeFilter)?.label?.toLowerCase() || "entries"} found` : "No activity yet"} subtitle={typeFilter !== "all" ? "Try a different filter or tax year." : "Expenses, distributions and settlements will appear here as a timeline."} />
      ) : (
        <Card className="p-0">
          <div className="divide-y divide-[#C2C9D1]">
            {entries.map((en, i) => {
              const m = TYPE_META[en.type]; const Icon = m.icon; const d = en.data;
              const isSettlement = en.type === "settlement";
              const isExpanded = isSettlement && expandedId === en.ref_id;
              return (
                <div key={en.ref_id + i} data-testid={`ledger-entry-${en.ref_id}`}>
                  <div
                    className={`flex items-center gap-3 px-4 py-3 ${isSettlement ? "cursor-pointer hover:bg-[#F8FAFC] transition-colors" : ""}`}
                    onClick={isSettlement ? () => setExpandedId(isExpanded ? null : en.ref_id) : undefined}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: `${m.color}1a`, color: m.color }}><Icon size={18} weight="bold" /></span>
                    <div className="min-w-0 flex-1">
                      {en.type === "expense" && (
                        <>
                          <div className="flex items-center gap-2"><span className="font-semibold">{d.payee}</span><StatusChip status={d.status} expense={d} /></div>
                          <div className="text-xs text-[#6b7280]">{fmtDate(en.date)} · {d.category} · {expenseRoutingLabel(d, parties, studentNick)}</div>
                        </>
                      )}
                      {en.type === "withdrawal" && (
                        <>
                          <div className="font-semibold">529 {d.mode === "lump_sum" ? "lump-sum" : "reimbursement"} distribution {d.is_exception ? <span className="text-[#F01919]">· exception</span> : ""}</div>
                          <div className="text-xs text-[#6b7280]">{fmtDate(en.date)} · to {d.actual_recipient === "party" ? nickFor(`party:${d.actual_recipient_party_id}`, parties, studentNick) : (d.actual_recipient === "other" ? (d.actual_recipient_other || "other") : (d.actual_recipient || d.intended_recipient))} {d.balance_prior != null ? `· bal ${money(d.balance_prior, "USD")}→${money(d.balance_after, "USD")}` : ""}</div>
                        </>
                      )}
                      {isSettlement && (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{nickFor(d.reimburser_ref, parties, studentNick) || "?"} → {nickFor(d.payor_ref, parties, studentNick) || "?"}</span>
                            <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: d.area === "529" ? "#034AA2" : "#6b7280" }}>{d.area === "529" ? "529" : "Private"}</span>
                          </div>
                          <div className="text-xs text-[#6b7280]">{fmtDate(en.date)}{(d.reimbursement_method || d.method) ? ` · ${d.reimbursement_method || d.method}` : ""}{(d.from_account || d.to_account) ? ` · ${d.from_account || "?"} → ${d.to_account || "?"}` : ""}</div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="tnum font-bold" style={{ color: m.color }}>{en.type === "expense" ? money(d.usd_amount, "USD") : money(d.amount, "USD")}</div>
                        {en.type === "expense" && d.original_currency !== "USD" && <div className="text-[10px] text-[#6b7280]">{money(d.original_amount, d.original_currency)}</div>}
                      </div>
                      {isSettlement && (
                        <CaretDown size={14} weight="bold" className={`shrink-0 text-[#6b7280] transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                      )}
                    </div>
                  </div>
                  {isExpanded && <SettlementDetail settlementId={en.ref_id} />}
                </div>
              );
            })}
          </div>
        </Card>
      ))}

      {isOwner && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#C2C9D1] bg-[#F7F8FA] px-4 py-3 text-xs text-[#6b7280]" data-testid="settlement-hint">
          <Info size={16} weight="bold" className="mt-0.5 shrink-0 text-[#034AA2]" />
          <span>Repayments are recorded from the Overview&apos;s settlement panel, gifts and loans under Money Envelopes.</span>
        </div>
      )}
    </div>
  );
}

function SettlementDetail({ settlementId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["settlement-detail", settlementId],
    queryFn: async () => (await api.get(`/settlements/${settlementId}/detail`)).data,
    enabled: !!settlementId,
  });

  if (isLoading) return <div className="px-4 pb-3 text-xs text-[#6b7280]">Loading details…</div>;
  if (!data) return null;

  const linked = data.linked_expenses || [];
  const hasLinked = linked.length > 0;
  const method = data.method || data.reimbursement_method;
  const note = data.note || data.notes;
  const hasMeta = method || data.from_account || data.to_account || note;

  return (
    <div className="border-t border-[#C2C9D1] bg-[#F8FAFC] px-4 pb-3 pt-2" data-testid={`settlement-detail-${settlementId}`}>
      {hasLinked ? (
        <>
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-[#077A39]">Linked expenses ({linked.length})</div>
          <div className="divide-y divide-[#E9E9EB] rounded-md border border-[#C2C9D1] bg-white">
            {linked.map((e) => (
              <div key={e.id} className="flex items-center gap-2 px-3 py-1.5 text-xs" data-testid={`settlement-linked-exp-${e.id}`}>
                <div className="min-w-0 flex-1">
                  <span className="font-semibold">{e.payee || "—"}</span>
                  <span className="ml-1.5 text-[#6b7280]">{fmtDate(e.transaction_date)}{e.category ? ` · ${e.category}` : ""}</span>
                </div>
                <div className="shrink-0 tnum font-bold">{money(e.usd_amount, "USD")}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="rounded-md border border-dashed border-[#D97706]/40 bg-[#FCF5EE] px-3 py-2 text-xs text-[#92600A]" data-testid="settlement-no-linked">
          Not linked to specific expenses — it still counts toward these parties&apos; totals. To link expenses, undo it from the Overview and re-record via the reconcile flow.
        </div>
      )}
      {hasMeta && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#6b7280]" data-testid="settlement-meta">
          {method && <span>Method: <b>{method}</b></span>}
          {data.from_account && <span>From: <b>{data.from_account}</b></span>}
          {data.to_account && <span>To: <b>{data.to_account}</b></span>}
          {note && <span>Note: <b>{note}</b></span>}
        </div>
      )}
    </div>
  );
}

function SummaryFig({ label, v, c, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">{label}</div>
      <div className="text-base font-extrabold tabular-nums" style={{ color: c }}>{money(v, "USD")}</div>
    </div>
  );
}


function FxReviewBanner({ studentId }) {
  const qc = useQueryClient();

  // Fetch FX needs-review expenses for this student
  const { data: allExpenses } = useQuery({
    queryKey: ["expenses-list", studentId],
    queryFn: async () => (await api.get(`/expenses?student_id=${studentId}`)).data,
    enabled: !!studentId,
  });

  // Fetch withdrawals for this student to find shortfalls
  const { data: allWithdrawals } = useQuery({
    queryKey: ["withdrawals-list", studentId],
    queryFn: async () => (await api.get(`/withdrawals?student_id=${studentId}`)).data,
    enabled: !!studentId,
  });

  const needsReview = (allExpenses || []).filter(
    (e) => e.fx_revision_log_legacy?.length > 0 && !e.fx_review_ack
  );
  const shortfallWds = (allWithdrawals || []).filter(
    (w) => (w.remaining ?? 0) < -0.005
  );

  if (!needsReview.length && !shortfallWds.length) return null;

  const markOne = async (expId) => {
    try {
      await api.patch(`/expenses/${expId}/fx-review-ack`);
      qc.invalidateQueries({ queryKey: ["expenses-list", studentId] });
      qc.invalidateQueries({ queryKey: ["ledger"] });
      toast.success("Marked as reviewed");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const markAll = async () => {
    try {
      await api.patch("/expenses/fx-review-ack-all");
      qc.invalidateQueries({ queryKey: ["expenses-list", studentId] });
      qc.invalidateQueries({ queryKey: ["ledger"] });
      toast.success(`Marked ${needsReview.length} as reviewed`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="fx-review-banner">
      {needsReview.length > 0 && (
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <Warning size={16} weight="bold" className="text-amber-600" />
            {needsReview.length} expense{needsReview.length !== 1 ? "s" : ""} had their exchange rate corrected to the payment-date rate. Review each one&apos;s coverage, then mark it reviewed.
          </div>
          <div className="mt-2 space-y-1.5">
            {needsReview.map((e) => {
              const legacy = e.fx_revision_log_legacy || [];
              const last = legacy[legacy.length - 1];
              const oldRate = last?.new_rate;
              const oldUsd = oldRate && e.original_amount ? (parseFloat(e.original_amount) * parseFloat(oldRate)).toFixed(2) : null;
              return (
                <div key={e.id} className="flex items-center gap-3 rounded-md border border-amber-200 bg-white px-3 py-1.5 text-xs" data-testid={`fx-review-row-${e.id}`}>
                  <span className="min-w-0 flex-1 truncate font-medium">{e.payee}</span>
                  <span className="text-[#6b7280]">{fmtDate(e.transaction_date)}</span>
                  {oldUsd && <span className="tnum text-amber-700">{money(Number(oldUsd), "USD")} → {money(e.usd_amount, "USD")}</span>}
                  <button onClick={() => markOne(e.id)} className="rounded px-2 py-0.5 text-[10px] font-bold text-amber-700 hover:bg-amber-100" data-testid={`fx-review-mark-${e.id}`}>
                    Mark reviewed
                  </button>
                </div>
              );
            })}
          </div>
          {needsReview.length > 1 && (
            <button onClick={markAll} className="mt-2 rounded-md border border-amber-400 px-3 py-1 text-xs font-bold text-amber-800 hover:bg-amber-100" data-testid="fx-review-mark-all">
              Mark all reviewed
            </button>
          )}
        </div>
      )}
      {shortfallWds.length > 0 && (
        <div className={needsReview.length > 0 ? "mt-3 border-t border-amber-200 pt-3" : ""}>
          <div className="flex items-center gap-2 text-sm font-semibold text-[#F01919]">
            <Warning size={16} weight="bold" />
            {shortfallWds.length} distribution{shortfallWds.length !== 1 ? "s" : ""} over-allocated — the linked expenses are worth more than was distributed.
          </div>
          <div className="mt-1.5 space-y-1">
            {shortfallWds.map((w) => (
              <div key={w.id} className="flex items-center gap-3 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs" data-testid={`fx-shortfall-wd-${w.id}`}>
                <span className="font-medium">{fmtDate(w.date_requested)}</span>
                <span className="tnum">Distributed {money(w.amount, "USD")}</span>
                <span className="font-semibold text-[#F01919] tnum">over-allocated by {money(Math.abs(w.remaining), "USD")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
